import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { CustomDialog, CustomIconButton, icon } from "../../../../../../../../../../common/components";
import styles from "./NoteActions.module.css";
import { translate } from "../../../../../../../../../../common/providers";
import { useApi, useTrackEvent } from "../../../../../../../../../../common/hooks";
import { NotesService } from "../../../../../../../../../../api";
import { isNonEmptyArray } from "../../../../../../../../../../common/utils";
import { setSelectedNote } from "../../../../../../../../slice/note/note-slice";
import ManualCreationDialog from "../../../../../note-creation-panel/manual-creation-dialog/ManualCreationDialog";

const NoteActions = ({ debouncedUpdateNote, onSetNoteStatus }) => {
	const { call: deleteNote } = useApi(NotesService.deleteNote);
	const { call: getAllNotes } = useApi(NotesService.getAllNotes);
	const { call: renameNote } = useApi(NotesService.renameNote);
	const { call: updateNoteStatus } = useApi(NotesService.updateNoteStatus);
	const userId = useSelector(({ context }) => context.user?.id);
	const projectId = useSelector(({ context }) => context.project?.id);
	const noteId = useSelector(({ note }) => note.selectedNote?.id);
	const noteTitle = useSelector(({ note }) => note.selectedNote?.title);
	const selectedNote = useSelector(({ note }) => note.selectedNote);
	const isEditable = useSelector(({ note }) => note.isEditable);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [openRenameDialog, setOpenRenameDialog] = useState(false);
	const dispatch = useDispatch();
	const trackEvent = useTrackEvent();
	const handleOpenDeleteDialog = () => {
		setOpenDeleteDialog(true);
	};
	const handleCloseDeleteDialog = () => {
		setOpenDeleteDialog(false);
	};
	const handleDelete = () => {
		deleteNote({ noteId })
			.then(() => {
				trackEvent("Notes_Delete_Note");
				dispatch(setSelectedNote({}));
				debouncedUpdateNote?.cancel();
				getAllNotes({ projectId }, { page: 0, limit: 1, sort: "LAST_MODIFICATION", dir: "DESC" }, {})
					.then((data) => {
						if (isNonEmptyArray(data.contents)) {
							dispatch(
								setSelectedNote({
									id: data.contents[0].id,
									title: data.contents[0].title,
									generatedByAI: data.contents[0].generatedByAI,
									lastModified: data.contents[0].lastModified,
									lastModifiedBy: data.contents[0].lastModifiedBy,
								})
							);
							if (data.contents[0].status === "OPENED") {
								updateNoteStatus({ noteId: data.contents[0].id }, { status: "LOCKED" })
									.then(() => {
										onSetNoteStatus({ noteId: data.contents[0].id, userId, status: "LOCKED" });
									})
									.catch(console.error);
							} else {
								onSetNoteStatus({
									noteId: data.contents[0].id,
									userId: data.contents[0].lastModifiedById,
									status: "LOCKED",
								});
							}
						} else {
							dispatch(setSelectedNote({}));
						}
					})
					.catch(console.error)
					.finally(() => {
						handleCloseDeleteDialog();
					});
			})
			.catch(console.error);
	};
	const handleOpenRenameDialog = () => {
		setOpenRenameDialog(true);
	};
	const handleCloseRenameDialog = () => {
		setOpenRenameDialog(false);
	};
	const handleRenameNote = (value) => {
		renameNote({ noteId }, { value })
			.then(() => {
				trackEvent("Notes_Rename");
				dispatch(setSelectedNote({ ...selectedNote, title: value }));
				handleCloseRenameDialog();
			})
			.catch(console.error);
	};
	return (
		<div className={styles.noteActions}>
			<CustomIconButton
				disabled={!isEditable}
				icon={icon.faPen}
				size="xs"
				variant="full-outlined"
				onClick={handleOpenRenameDialog}
			/>
			<CustomIconButton
				disabled={!isEditable}
				icon={icon.faTrash}
				size="xs"
				variant="outlined"
				onClick={handleOpenDeleteDialog}
			/>

			<CustomDialog
				className={styles.deleteDialog}
				classNameRoot={styles.deleteDialog__root}
				closeLabel={translate("common:btn.cancel")}
				iconColor="var(--red-500)"
				iconModel={icon.faExclamationTriangle}
				open={openDeleteDialog}
				submitColor="destructive"
				submitLabel={translate("common:btn.delete")}
				subTitle={translate("notes.right-panel.header.actions.delete.subtitle", { title: noteTitle })}
				title={translate("notes.right-panel.header.actions.delete.title")}
				onClose={handleCloseDeleteDialog}
				onSubmit={handleDelete}
			/>
			<ManualCreationDialog
				defaultValue={noteTitle}
				label={translate("notes.action.create.placeholder")}
				open={openRenameDialog}
				submitLabel={translate("common:btn.confirm")}
				title={translate("notes.right-panel.header.actions.rename-title")}
				onClose={handleCloseRenameDialog}
				onSubmit={handleRenameNote}
			/>
		</div>
	);
};

export default NoteActions;
